/**
 * ContactLogic - Service functions for Contact POJO operations
 */

import type { Contact, ContactList, CreateContactResult } from './types';
import type { Phone } from '@/modules/phone';
import { PhoneLogic } from '@/modules/phone';
import type { ContactLike } from '@snowball/meprfo';
import { generateUUID } from '@/util/uuid';
import type { DeviceInfo } from '@/hooks/useDeviceInfo';
import { validateContact, extractUUID } from './schema';
import type { DuplicateContact, DuplicateContactGroup } from './reducer';

/**
 * Check if a string starts with a known prefix (case-insensitive)
 * Returns the prefix if found, empty string otherwise
 */
function extractPrefix(name: string): string {
  const normalized = name.trim();
  if (!normalized) return '';

  // Multi-word prefixes (must be checked first)
  const multiWordPrefixes = ['the honorable', 'the hon'];

  const lower = normalized.toLowerCase();
  for (const prefix of multiWordPrefixes) {
    if (lower.startsWith(prefix)) {
      const match = normalized.substring(0, prefix.length);
      return match;
    }
  }

  // Single-word prefixes
  const prefixes = [
    'mr',
    'mrs',
    'miss',
    'ms',
    'mx',
    'dr',
    'prof',
    'rev',
    'fr',
    'sr',
    'br',
    'hon',
    'the',
    'capt',
    'col',
    'gen',
    'lt',
    'maj',
    'sgt',
    'cpl',
    'pvt',
    'adm',
    'cdr',
    'ens',
    'atty',
    'esq',
    'cpa',
    'sir',
    'dame',
    'lord',
    'lady',
    'baron',
    'baroness',
    'count',
    'countess',
    'duke',
    'duchess',
    'prince',
    'princess',
    'king',
    'queen',
    'emperor',
    'empress',
    'rabbi',
    'imam',
    'pastor',
    'bishop',
    'archbishop',
    'cardinal',
    'pope',
    'swami',
    'guru',
  ];

  // Check for prefix with optional period
  for (const prefix of prefixes) {
    const prefixWithPeriod = prefix + '.';
    if (
      lower.startsWith(prefix + ' ') ||
      lower.startsWith(prefixWithPeriod + ' ') ||
      lower === prefix ||
      lower === prefixWithPeriod
    ) {
      // Extract the actual prefix from the original string
      const match = normalized.match(new RegExp(`^${prefix}\\.?\\s*`, 'i'));
      if (match) {
        return match[0].trim();
      }
    }
  }

  return '';
}

/**
 * Check if a string ends with a known suffix (case-insensitive)
 * Returns the suffix if found, empty string otherwise
 */
function extractSuffix(name: string): string {
  const normalized = name.trim();
  if (!normalized) return '';

  const suffixes = [
    'jr',
    'junior',
    'sr',
    'senior',
    'ii',
    'iii',
    'iv',
    'v',
    'vi',
    'vii',
    'viii',
    'ix',
    'x',
    '2nd',
    '3rd',
    '4th',
    '5th',
    '6th',
    '7th',
    '8th',
    '9th',
    '10th',
    'md',
    'phd',
    'dphil',
    'dds',
    'dmd',
    'jd',
    'esq',
    'cpa',
    'rn',
    'dvm',
    'do',
    'dc',
    'od',
    'pharmd',
    'dpm',
    'psyd',
    'edd',
    'dsc',
    'deng',
  ];

  // Build a regex fragment that matches the suffix with optional periods between letters.
  // e.g. "md" matches "md", "m.d", "m.d.", "M.D." etc.
  function suffixPattern(suffix: string): string {
    return suffix
      .split('')
      .map(ch => ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('\\.?');
  }

  // Check for suffix with optional period, optionally preceded by comma or space
  for (const suffix of suffixes) {
    const pat = suffixPattern(suffix);
    const patterns = [
      new RegExp(`[,\\s]+${pat}\\.?$`, 'i'),
      new RegExp(`^${pat}\\.?$`, 'i'),
    ];

    for (const pattern of patterns) {
      const match = normalized.match(pattern);
      if (match) {
        return match[0].trim().replace(/^[, ]+/, '');
      }
    }
  }

  return '';
}

export const ContactLogic = {
  /**
   * Get most highly preferred phone number
   * First tries mobile, then falls back to non-mobile
   */
  getPrimaryPhone(contact: Contact): string | null {
    // Only consider valid US phone numbers
    const usTels = contact.tels.filter(t => PhoneLogic.isValidUS(t));
    if (usTels.length === 0) {
      return null;
    }

    // Sort function for preference (lower number = higher preference)
    const sortByPref = (a: Phone, b: Phone): number => {
      const prefA = a.pref ?? Number.MAX_SAFE_INTEGER;
      const prefB = b.pref ?? Number.MAX_SAFE_INTEGER;
      return prefA - prefB;
    };

    // First try cell numbers
    const cellTels = usTels.filter(t => PhoneLogic.getKind(t.type) === 'cell');
    if (cellTels.length > 0) {
      const sorted = [...cellTels].sort(sortByPref);
      return sorted[0].value;
    }

    // Fall back to non-cell numbers
    const nonCellTels = usTels.filter(
      t => PhoneLogic.getKind(t.type) !== 'cell'
    );
    if (nonCellTels.length > 0) {
      const sorted = [...nonCellTels].sort(sortByPref);
      return sorted[0].value;
    }

    return null;
  },

  /**
   * Create an sms: link for the given contacts and message body.
   * Uses Apple-specific format (sms://open?addresses=) on iOS and macOS.
   * Uses Android format (sms:number1;number2?body=) elsewhere.
   */
  createSMSLink(
    contacts: ContactList,
    body: string,
    deviceInfo: DeviceInfo
  ): string {
    const phones = contacts
      .map(c => ContactLogic.getPrimaryPhone(c))
      .filter((p): p is string => p !== null)
      .map(p => p.replace(/\D/g, '')) // Remove all non-digits
      .filter(p => p.length > 0);

    const encodedBody = encodeURIComponent(body);
    const isApple = deviceInfo.isIOS || deviceInfo.isMacOS;

    if (isApple) {
      const recipientString = phones.join(',');
      return `sms://open?addresses=${recipientString}&body=${encodedBody}`;
    }
    const recipientString = phones.join(';');
    return `sms:${recipientString}?body=${encodedBody}`;
  },

  /**
   * Format phone number
   * @param contact - The contact to get the phone number from
   * @param localId - Optional localId of the specific phone number to format. If not provided, returns the primary phone.
   * @returns Formatted phone number string or null if not found
   */
  getFormattedPhone(contact: Contact, localId?: string): string | null {
    if (localId !== undefined) {
      // Find the specific phone number by localId
      const tel = contact.tels.find(t => t.localId === localId);
      if (!tel) {
        return null;
      }
      return PhoneLogic.format(tel.value);
    }

    // Default behavior: return primary phone
    const primary = ContactLogic.getPrimaryPhone(contact);
    return primary ? PhoneLogic.format(primary) : null;
  },

  /**
   * Check if contact1's phone set is a subset of (or equal to) contact2's phone set.
   * Comparison is by normalized value only, ignoring phone type.
   * An empty phone set is a subset of any phone set.
   */
  hasPhoneSubsetOf(contact1: Contact, contact2: Contact): boolean {
    if (contact1.tels.length === 0) return true;
    if (contact2.tels.length === 0) return false;
    const contact2Phones = new Set(
      contact2.tels.map(tel => PhoneLogic.normalize(tel.value))
    );
    return contact1.tels.every(tel =>
      contact2Phones.has(PhoneLogic.normalize(tel.value))
    );
  },

  /**
   * Get full name from contact, concatenating all name parts with spaces
   * @param contact - The contact to get the full name from
   * @returns Full name string or undefined if no name parts are present
   */
  fullName(contact: Contact): string | undefined {
    const parts = [
      contact.prefix,
      contact.firstName,
      contact.middleName,
      contact.lastName,
      contact.suffix,
    ].filter(p => p.trim());

    if (parts.length > 0) {
      return parts.join(' ').trim();
    }

    if (contact.nickname.trim()) {
      return contact.nickname.trim();
    }

    // Fall back to the company name for businesses with no personal name.
    const company = contact.companyName?.trim();
    if (company) {
      return company;
    }

    return undefined;
  },

  /**
   * Recombine the organization components (company, unit, sub-unit) into a
   * structured vCard ORG value (`Company;Unit;SubUnit`). Trailing empty
   * components are dropped so a company-only contact serializes to `Company`.
   * @returns The ORG value, or undefined when no component is present
   */
  organizationVCardValue(contact: Contact): string | undefined {
    const components = [
      contact.companyName ?? '',
      contact.department ?? '',
      contact.subDepartment ?? '',
    ];
    while (components.length > 0 && !components[components.length - 1].trim()) {
      components.pop();
    }
    if (components.length === 0) return undefined;
    return components
      .map(component => ContactLogic.escapeVCardValue(component))
      .join(';');
  },

  /**
   * Get display name with fallback heuristics:
   * - If full name is present, return it
   * - If nickname is present, return it
   * - If no name parts or nickname are present, return formatted primary phone
   * - If formatted primary phone is not present, return empty string
   */
  displayName(contact: Contact): string {
    return (
      ContactLogic.fullName(contact) ||
      ContactLogic.getFormattedPhone(contact) ||
      ''
    );
  },

  /**
   * Informal name for personal greetings: nickname → first name → full name.
   * Returns '' when the contact has no usable name.
   */
  informalName(contact: Contact): string {
    return (
      contact.nickname.trim() ||
      contact.firstName.trim() ||
      ContactLogic.fullName(contact) ||
      ''
    );
  },

  /**
   * Get 1-2 character initials for a contact avatar.
   * - Both first+last name: use their initials (e.g. "JD")
   * - Multi-word display name: first letter of first two words
   * - Single word: first two characters (e.g. "Jo")
   * - Single character: just that character
   * - No display name: "?"
   */
  getInitials(contact: Contact): string {
    const first = contact.firstName.trim();
    const last = contact.lastName.trim();
    if (first && last) {
      return (first.charAt(0) + last.charAt(0)).toUpperCase();
    }

    const name = ContactLogic.fullName(contact);
    if (name) {
      return name.slice(0, 2).toUpperCase();
    }

    const phone = ContactLogic.getPrimaryPhone(contact);
    if (phone) {
      const digits = phone.replace(/\D/g, '');
      if (digits.length >= 2) return digits.slice(0, 2);
    }

    return '?';
  },

  /**
   * Build a search string from all name components concatenated in order.
   * Used for substring search so that queries like "John Sm" match a contact
   * with firstName="John" and lastName="Smith". The company name is included
   * so business contacts are searchable by company; the organizational unit and
   * sub-unit are excluded since they are never shown on a contact card, which
   * would make a search hit on them confusing.
   */
  nameSearchText(contact: Contact): string {
    return [
      contact.prefix,
      contact.firstName,
      contact.middleName,
      contact.lastName,
      contact.suffix,
      contact.nickname,
      contact.companyName,
    ]
      .filter((p): p is string => Boolean(p?.trim()))
      .join(' ');
  },

  /**
   * Check if contact has a name
   */
  hasName(contact: Contact): boolean {
    if (contact.nickname.trim()) {
      return true;
    }
    if (
      contact.prefix.trim() ||
      contact.firstName.trim() ||
      contact.middleName.trim() ||
      contact.lastName.trim() ||
      contact.suffix.trim()
    ) {
      return true;
    }
    return false;
  },

  /**
   * Returns a comparator (for use with Array.sort) that orders contact IDs
   * by last name then first name, given a reference array of Contact objects.
   */
  lastNameComparator(contacts: Contact[]): (a: string, b: string) => number {
    const byId = new Map(contacts.map(c => [c.localId, c]));
    return (a, b) => {
      const ca = byId.get(a);
      const cb = byId.get(b);
      const cmp = (ca?.lastName ?? '').localeCompare(cb?.lastName ?? '');
      return cmp !== 0
        ? cmp
        : (ca?.firstName ?? '').localeCompare(cb?.firstName ?? '');
    };
  },

  /**
   * Create a new Contact with generated localId and uid.
   * Callers cannot pass localId or uid — those are always synthesized.
   */
  create(fields?: Partial<Omit<Contact, 'localId' | 'uid'>>): Contact {
    const localId = generateUUID();
    return {
      prefix: '',
      firstName: '',
      middleName: '',
      lastName: '',
      suffix: '',
      nickname: '',
      tels: [],
      listingCategories: [],
      ...fields,
      localId,
      uid: 'urn:uuid:' + localId,
    };
  },

  /**
   * Unescape a vCard text value per RFC 6350 §3.4:
   * `\\` → `\`, `\,` → `,`, `\;` → `;`, and `\n`/`\N` → newline.
   */
  unescapeVCardValue(value: string): string {
    return value.replace(/\\(.)/g, (_match, ch) =>
      ch === 'n' || ch === 'N' ? '\n' : ch
    );
  },

  /**
   * Escape a vCard text value per RFC 6350 §3.4: backslash, comma, semicolon,
   * and newline are backslash-escaped. Backslash is escaped first so the
   * escapes added for the other characters are not themselves doubled.
   */
  escapeVCardValue(value: string): string {
    return value
      .replace(/\\/g, '\\\\')
      .replace(/\n/g, '\\n')
      .replace(/,/g, '\\,')
      .replace(/;/g, '\\;');
  },

  /**
   * Split a vCard structured value (e.g. N or ORG) on its unescaped `;`
   * component delimiter. Escaped delimiters (`\;`) stay inside their component;
   * unescaping each component is done separately by the caller.
   */
  splitVCardComponents(value: string): string[] {
    const components: string[] = [];
    let current = '';
    for (let i = 0; i < value.length; i++) {
      const ch = value[i];
      if (ch === '\\' && i + 1 < value.length) {
        // Preserve the escape sequence (backslash + next char) intact.
        current += ch + value[i + 1];
        i++;
      } else if (ch === ';') {
        components.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
    components.push(current);
    return components;
  },

  /**
   * Generate vCard 4.0 format string
   */
  toVCard(contact: Contact): string {
    const parts = contact.vCardParts;
    const lines: string[] = parts
      ? [...parts.header]
      : ['BEGIN:VCARD', 'VERSION:4.0'];

    // UID
    lines.push(`UID:${contact.uid ?? 'urn:uuid:' + contact.localId}`);

    // FN / N handling
    if (contact.fnPassthru) {
      lines.push(`FN:${ContactLogic.escapeVCardValue(contact.fnPassthru)}`);
    }
    const n = [
      contact.lastName,
      contact.firstName,
      contact.middleName,
      contact.prefix,
      contact.suffix,
    ]
      .map(part => ContactLogic.escapeVCardValue(part))
      .join(';');
    lines.push(`N:${n}`);

    // NICKNAME
    if (contact.nickname.trim()) {
      lines.push(
        `NICKNAME:${ContactLogic.escapeVCardValue(contact.nickname.trim())}`
      );
    }

    // ORG (Organization): recombine the company/unit/sub-unit components into
    // the structured ;-delimited value, dropping trailing empty components.
    const orgValue = ContactLogic.organizationVCardValue(contact);
    if (orgValue) {
      lines.push(`ORG:${orgValue}`);
    }

    // TEL lines
    for (const tel of contact.tels) {
      const typeStr = tel.type
        ? PhoneLogic.toVCardTypeString(tel.type)
        : 'phone';
      let telLine = `TEL;TYPE=${typeStr}`;
      if (tel.pidPassthru) {
        telLine += `;PID=${tel.pidPassthru}`;
      }
      if (tel.pref !== undefined) {
        telLine += `;PREF=${tel.pref}`;
      }
      telLine += `:${tel.value}`;
      lines.push(telLine);
    }

    // CATEGORIES
    if (contact.listingCategories.length > 0) {
      lines.push(`CATEGORIES:${contact.listingCategories.join(',')}`);
    }

    // Snowball custom fields
    if (contact.snowballId) {
      lines.push(`X-SNOWBALL-ID:${contact.snowballId}`);
    }
    if (contact.lastSnowballRefreshDate) {
      lines.push(`X-SNOWBALL-REFRESH-DATE:${contact.lastSnowballRefreshDate}`);
    }

    // Unparsed lines from original vCard
    if (parts) {
      lines.push(...parts.unparsedLines);
    }

    lines.push(...(parts ? parts.footer : ['END:VCARD']));
    return lines.join('\n');
  },

  /**
   * Parse vCard 4.0 format string
   */
  fromVCard(vcardText: string): Contact | null {
    try {
      // Unfold continuation lines (RFC 6350 §3.2): a line starting with a
      // space or tab is a continuation of the previous logical line.
      const rawLines = vcardText.split(/\r?\n/);
      const lines: string[] = [];
      for (const raw of rawLines) {
        if ((raw.startsWith(' ') || raw.startsWith('\t')) && lines.length > 0) {
          lines[lines.length - 1] += raw.substring(1);
        } else {
          lines.push(raw);
        }
      }
      let prefix = '';
      let firstName = '';
      let middleName = '';
      let lastName = '';
      let suffix = '';
      let nickname = '';
      let companyName: string | undefined;
      let department: string | undefined;
      let subDepartment: string | undefined;
      let uid = '';
      let snowballId: string | undefined;
      let lastSnowballRefreshDate: string | undefined;
      const tels: Phone[] = [];
      const listingCategories: string[] = [];
      let hasFN = false;
      let fnValue = '';

      // Collect header (BEGIN:VCARD, VERSION:, and any lines before first property)
      const header: string[] = [];
      const footer: string[] = [];
      const unparsedLines: string[] = [];

      // Phase 1: Identify header lines (up to and including BEGIN: and VERSION:)
      let i = 0;
      for (; i < lines.length; i++) {
        const trimmed = lines[i].trim();
        if (
          !trimmed ||
          trimmed.startsWith('BEGIN:') ||
          trimmed.startsWith('VERSION:')
        ) {
          header.push(lines[i]);
        } else {
          break; // First real property line
        }
      }

      // Phase 2: Identify footer lines (END: and anything after)
      let lastPropertyIndex = lines.length - 1;
      for (let j = lines.length - 1; j >= i; j--) {
        const trimmed = lines[j].trim();
        if (!trimmed || trimmed.startsWith('END:')) {
          footer.unshift(lines[j]);
          lastPropertyIndex = j - 1;
        } else {
          break;
        }
      }

      // Phase 3: Parse property lines between header and footer
      for (let k = i; k <= lastPropertyIndex; k++) {
        const trimmed = lines[k].trim();
        if (!trimmed) {
          unparsedLines.push(lines[k]);
          continue;
        }

        let parsed = false;

        // Extract FN (Formatted Name)
        if (trimmed.startsWith('FN:')) {
          fnValue = ContactLogic.unescapeVCardValue(
            trimmed.substring(3).trim()
          );
          hasFN = true;
          parsed = true;
        }

        // Extract N (Name) - lastName;firstName;middleName;prefix;suffix
        if (trimmed.startsWith('N:')) {
          const nameParts = ContactLogic.splitVCardComponents(
            trimmed.substring(2)
          ).map(part => ContactLogic.unescapeVCardValue(part.trim()));
          lastName = nameParts[0] || '';
          firstName = nameParts[1] || '';
          middleName = nameParts[2] || '';
          prefix = nameParts[3] || '';
          suffix = nameParts[4] || '';
          parsed = true;
        }

        // Extract UID
        if (trimmed.startsWith('UID:')) {
          uid = trimmed.substring(4).trim();
          parsed = true;
        }

        // Extract NICKNAME
        if (trimmed.startsWith('NICKNAME:')) {
          nickname = ContactLogic.unescapeVCardValue(
            trimmed.substring(9).trim()
          );
          parsed = true;
        }

        // Extract ORG (Organization): split the structured value on its
        // unescaped ';' delimiter into company, organizational unit, and
        // sub-unit components, unescaping each.
        if (trimmed.startsWith('ORG:')) {
          const orgParts = ContactLogic.splitVCardComponents(
            trimmed.substring(4)
          ).map(part => ContactLogic.unescapeVCardValue(part.trim()));
          companyName = orgParts[0] || '';
          department = orgParts[1] || '';
          subDepartment = orgParts[2] || '';
          parsed = true;
        }

        // Extract TEL (Telephone)
        if (trimmed.startsWith('TEL')) {
          // Parse format: TEL;TYPE=mobile;PREF=1:555-123-4567 or TEL;TYPE=mobile:555-123-4567
          const telMatch = trimmed.match(/TEL(?:;([^:]+))?:(.+)/);
          if (telMatch) {
            const params = telMatch[1] || '';
            const value = telMatch[2].trim();

            let type: Phone['type'] = PhoneLogic.PHONE_TYPES.phone;
            let pref: number | undefined = undefined;
            let pidPassthru: string | undefined = undefined;

            // Parse TYPE parameter
            const typeMatch = params.match(/TYPE=([^;]+)/);
            if (typeMatch) {
              type = PhoneLogic.fromVCardTypeString(typeMatch[1]);
            }

            // Parse PREF parameter
            const prefMatch = params.match(/PREF=(\d+)/);
            if (prefMatch) {
              pref = parseInt(prefMatch[1], 10);
            }

            // Parse PID parameter
            const pidMatch = params.match(/PID=([^;]+)/);
            if (pidMatch) {
              pidPassthru = pidMatch[1];
            }

            tels.push({
              localId: generateUUID(),
              value,
              type,
              pref,
              ...(pidPassthru && { pidPassthru }),
            });
            parsed = true;
          }
        }

        // Extract CATEGORIES
        if (trimmed.startsWith('CATEGORIES:')) {
          const cats = trimmed
            .substring(11)
            .split(',')
            .map(c => c.trim());
          listingCategories.push(...cats);
          parsed = true;
        }

        // Extract Snowball custom fields
        if (trimmed.startsWith('X-SNOWBALL-ID:')) {
          snowballId = trimmed.substring('X-SNOWBALL-ID:'.length).trim();
          parsed = true;
        }
        if (trimmed.startsWith('X-SNOWBALL-REFRESH-DATE:')) {
          lastSnowballRefreshDate = trimmed
            .substring('X-SNOWBALL-REFRESH-DATE:'.length)
            .trim();
          parsed = true;
        }

        if (!parsed) {
          unparsedLines.push(lines[k]);
        }
      }

      // Create contact if we have at least one tel
      if (tels.length === 0) {
        return null;
      }

      // Parse FN slot if present
      if (hasFN && fnValue) {
        const nameParts = ContactLogic.parseNameIntoParts(fnValue);
        prefix = nameParts.prefix;
        firstName = nameParts.firstName;
        middleName = nameParts.middleName;
        lastName = nameParts.lastName;
        suffix = nameParts.suffix;
      }

      let localId: string;
      if (uid) {
        const extracted = extractUUID(uid);
        localId = extracted ?? generateUUID();
      } else {
        localId = generateUUID();
        uid = 'urn:uuid:' + localId;
      }

      return {
        localId,
        uid,
        prefix: prefix || '',
        firstName: firstName || '',
        middleName: middleName || '',
        lastName: lastName || '',
        suffix: suffix || '',
        nickname: nickname || '',
        ...(companyName && { companyName }),
        ...(department && { department }),
        ...(subDepartment && { subDepartment }),
        tels,
        listingCategories,
        ...(hasFN && fnValue ? { fnPassthru: fnValue } : {}),
        ...(snowballId && { snowballId }),
        ...(lastSnowballRefreshDate && { lastSnowballRefreshDate }),
        vCardParts: { header, footer, unparsedLines },
      };
    } catch {
      return null;
    }
  },

  /**
   * Serialize contact to JSON format
   */
  toJSON(contact: Contact): {
    localId: string;
    uid?: string;
    prefix: string;
    firstName: string;
    middleName: string;
    lastName: string;
    suffix: string;
    nickname: string;
    companyName?: string;
    department?: string;
    subDepartment?: string;
    tels: Phone[];
    listingCategories: string[];
    fnPassthru?: string;
    vCardParts?: {
      header: string[];
      footer: string[];
      unparsedLines: string[];
    };
  } {
    return {
      localId: contact.localId,
      uid: contact.uid,
      prefix: contact.prefix,
      firstName: contact.firstName,
      middleName: contact.middleName,
      lastName: contact.lastName,
      suffix: contact.suffix,
      nickname: contact.nickname,
      ...(contact.companyName !== undefined && {
        companyName: contact.companyName,
      }),
      ...(contact.department !== undefined && {
        department: contact.department,
      }),
      ...(contact.subDepartment !== undefined && {
        subDepartment: contact.subDepartment,
      }),
      tels: [...contact.tels],
      listingCategories: [...contact.listingCategories],
      ...(contact.fnPassthru !== undefined && {
        fnPassthru: contact.fnPassthru,
      }),
      ...(contact.snowballId !== undefined && {
        snowballId: contact.snowballId,
      }),
      ...(contact.lastSnowballRefreshDate !== undefined && {
        lastSnowballRefreshDate: contact.lastSnowballRefreshDate,
      }),
      ...(contact.vCardParts !== undefined && {
        vCardParts: {
          header: [...contact.vCardParts.header],
          footer: [...contact.vCardParts.footer],
          unparsedLines: [...contact.vCardParts.unparsedLines],
        },
      }),
    };
  },

  /**
   * Deserialize contact from JSON format with validation
   */
  fromJSON(json: unknown): Contact | null {
    return validateContact(json);
  },

  /**
   * Factory function to create a Contact
   * Parses formattedName into name parts (prefix, firstName, middleName, lastName, suffix)
   * Returns null if there are no valid phone numbers
   * Otherwise returns a CreateContactResult with the contact and duplicate phone numbers
   */
  createReachableFromFormattedName(
    formattedName: string,
    phoneValues: string[]
  ): CreateContactResult | null {
    // Parse formattedName into parts
    const nameParts = ContactLogic.parseNameIntoParts(formattedName);

    // Process phone values into Phone objects
    const tels: Phone[] = [];
    for (const phoneValue of phoneValues) {
      tels.push({
        localId: generateUUID(),
        value: phoneValue,
        type: PhoneLogic.PHONE_TYPES.phone,
      });
    }

    // Delegate to createReachable with parsed name parts and tels
    return ContactLogic.createReachable({
      ...nameParts,
      tels,
    });
  },

  /**
   * Creates a contact from a partially-filled out contact. Requires at least
   * one phone number (US or international) and deduplicates phones.
   * Returns null if no phone numbers are provided.
   */
  createReachable(
    contactPartial: Partial<Contact>
  ): CreateContactResult | null {
    const {
      prefix = '',
      firstName = '',
      middleName = '',
      lastName = '',
      suffix = '',
      nickname = '',
      companyName,
      listingCategories = [],
      tels = [],
    } = contactPartial;

    if (tels.length === 0) {
      return null;
    }

    const duplicatePhones: string[] = [];

    const updatedTels: Phone[] = [];
    for (const t of tels) {
      // Reject values with no digits (empty strings, non-phone text like "invalid")
      if (!/\d/.test(t.value)) {
        continue;
      }
      // Deduplicate: same normalized value AND same type kind
      const isDuplicate = updatedTels.some(
        t2 =>
          PhoneLogic.normalize(t2.value) === PhoneLogic.normalize(t.value) &&
          PhoneLogic.getKind(t2.type) === PhoneLogic.getKind(t.type)
      );
      if (isDuplicate) {
        duplicatePhones.push(t.value);
      } else {
        updatedTels.push({
          ...t,
          localId: t.localId || generateUUID(),
        });
      }
    }

    if (updatedTels.length === 0) {
      return null;
    }

    // A contact is only "reachable" if it has at least one US phone we can SMS.
    // A number that is solely non-US (e.g. +233…) is not deliverable through the
    // US-only sharing pipeline, so don't create a contact for it.
    if (!updatedTels.some(t => PhoneLogic.isValidUS(t))) {
      return null;
    }

    const contact = ContactLogic.create({
      prefix,
      firstName,
      middleName,
      lastName,
      suffix,
      nickname,
      listingCategories,
      tels: updatedTels,
      ...(companyName?.trim() && { companyName: companyName.trim() }),
    });

    return {
      contact,
      duplicatePhones: duplicatePhones,
    };
  },

  /**
   * Parse a name string into name parts (prefix, firstName, middleName, lastName, suffix)
   * Handles extraction of prefixes and suffixes, then splits remaining name into parts
   */
  parseNameIntoParts(name: string): {
    prefix: string;
    firstName: string;
    middleName: string;
    lastName: string;
    suffix: string;
  } {
    let nameToParse = name.trim();
    if (!nameToParse) {
      return {
        prefix: '',
        firstName: '',
        middleName: '',
        lastName: '',
        suffix: '',
      };
    }

    let prefix = '';
    let suffix = '';

    // Extract prefix
    const extractedPrefix = extractPrefix(nameToParse);
    if (extractedPrefix) {
      prefix = extractedPrefix;
      nameToParse = nameToParse.substring(extractedPrefix.length).trim();
    }

    // Extract suffix
    const extractedSuffix = extractSuffix(nameToParse);
    if (extractedSuffix) {
      suffix = extractedSuffix;
      nameToParse = nameToParse
        .replace(
          new RegExp(
            `[,\\s]*${extractedSuffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\.?$`,
            'i'
          ),
          ''
        )
        .trim();
    }

    // Split remaining name into words
    const words = nameToParse.split(/\s+/).filter(w => w.trim());

    let firstName = '';
    let middleName = '';
    let lastName = '';

    if (words.length > 0) {
      // First word is firstName
      firstName = words[0];
      // Last word is lastName
      if (words.length > 1) {
        lastName = words[words.length - 1];
        // Everything in between is middleName
        if (words.length > 2) {
          middleName = words.slice(1, -1).join(' ');
        }
      }
    }

    return { prefix, firstName, middleName, lastName, suffix };
  },

  /**
   * Build a Contact prefill from whatever the user typed before tapping
   * "Add as new contact". A phone-like input routes to a mobile tel; anything
   * else is parsed as a name.
   */
  createPartialFromHint(hint: string): Contact {
    const trimmed = hint.trim();
    const parsed = ContactLogic.parseNameIntoParts(hint);
    const isPhoneDraft = PhoneLogic.isPartialUSPhone(trimmed);
    return {
      localId: '',
      prefix: isPhoneDraft ? '' : parsed.prefix,
      firstName: isPhoneDraft ? '' : parsed.firstName,
      middleName: isPhoneDraft ? '' : parsed.middleName,
      lastName: isPhoneDraft ? '' : parsed.lastName,
      suffix: isPhoneDraft ? '' : parsed.suffix,
      nickname: '',
      tels: isPhoneDraft
        ? [
            {
              localId: '',
              value: trimmed,
              type: PhoneLogic.PHONE_TYPES.cell,
            },
          ]
        : [],
      listingCategories: [],
    };
  },

  /**
   * Check if two contacts match by name
   * Matches if:
   * - Same first and last name (case-insensitive), OR
   * - Same nickname (case-insensitive), OR
   * - If one has a nickname, parse it and check if parsed first+last matches the other's first+last
   */
  matchByCommonNameParts(contact1: Contact, contact2: Contact): boolean {
    // Normalize names for comparison - handle undefined/null values
    const normalize = (s: string | undefined): string => {
      if (!s) return '';
      return s.trim().toLowerCase();
    };

    const c1First = normalize(contact1.firstName);
    const c1Last = normalize(contact1.lastName);
    const c1Nick = normalize(contact1.nickname);

    const c2First = normalize(contact2.firstName);
    const c2Last = normalize(contact2.lastName);
    const c2Nick = normalize(contact2.nickname);

    // Match if first and last names are the same
    if (c1First && c1Last && c2First && c2Last) {
      if (c1First === c2First && c1Last === c2Last) {
        return true;
      }
    }

    // Match if nicknames are the same (and non-empty).
    // If both contacts also have last names, require last names to match too —
    // a shared first-name-only nickname (e.g. "Chris") is not sufficient to
    // identify two people with different surnames as duplicates.
    if (c1Nick && c2Nick && c1Nick === c2Nick) {
      if (c1Last && c2Last) {
        if (c1Last === c2Last) return true;
      } else {
        return true;
      }
    }

    // If contact1 has a nickname, parse it and check against contact2's first+last
    if (c1Nick) {
      const parsed = ContactLogic.parseNameIntoParts(contact1.nickname);
      const parsedFirst = normalize(parsed.firstName);
      const parsedLast = normalize(parsed.lastName);
      if (parsedFirst && parsedLast && c2First && c2Last) {
        if (parsedFirst === c2First && parsedLast === c2Last) {
          return true;
        }
      }
    }

    // If contact2 has a nickname, parse it and check against contact1's first+last
    if (c2Nick) {
      const parsed = ContactLogic.parseNameIntoParts(contact2.nickname);
      const parsedFirst = normalize(parsed.firstName);
      const parsedLast = normalize(parsed.lastName);
      if (parsedFirst && parsedLast && c1First && c1Last) {
        if (parsedFirst === c1First && parsedLast === c1Last) {
          return true;
        }
      }
    }

    // Phone-only fallback: if neither contact has any name data, match by shared phone number
    const hasName = (c: Contact) => !!(c.firstName || c.lastName || c.nickname);
    if (!hasName(contact1) && !hasName(contact2)) {
      if (contact1.tels.length > 0 && contact2.tels.length > 0) {
        const c2Phones = new Set(
          contact2.tels.map(tel => PhoneLogic.normalize(tel.value))
        );
        if (
          contact1.tels.some(tel =>
            c2Phones.has(PhoneLogic.normalize(tel.value))
          )
        ) {
          return true;
        }
      }
    }

    return false;
  },

  /**
   * Find contacts with matching names, focused on incoming contacts
   * Returns groups of duplicates as ContactToResolveGroup arrays
   * Groups incoming contacts by name matching (first+last name or nickname), then finds matching existing contacts
   */
  findDuplicatesByName(
    existing: ContactList,
    incoming: ContactList
  ): DuplicateContactGroup[] {
    const groups: DuplicateContactGroup[] = [];

    // Build a set of existing IDs for O(1) lookup
    const existingIds = new Set(existing.map(c => c.localId));

    // Deduplicate incoming by localId, excluding any whose ID is already in existing
    const incomingIds = new Set<string>();
    const uniqueIncoming = incoming.filter(c => {
      if (existingIds.has(c.localId)) return false;
      if (incomingIds.has(c.localId)) return false;
      incomingIds.add(c.localId);
      return true;
    });

    // Group incoming contacts by name matching
    const incomingGroups: Contact[][] = [];
    const processedIncoming = new Set<string>();

    for (let i = 0; i < uniqueIncoming.length; i++) {
      if (processedIncoming.has(uniqueIncoming[i].localId)) continue;

      const group: Contact[] = [uniqueIncoming[i]];
      processedIncoming.add(uniqueIncoming[i].localId);

      // Find all other incoming contacts that match this one
      for (let j = i + 1; j < uniqueIncoming.length; j++) {
        if (processedIncoming.has(uniqueIncoming[j].localId)) continue;
        if (
          ContactLogic.matchByCommonNameParts(
            uniqueIncoming[i],
            uniqueIncoming[j]
          )
        ) {
          group.push(uniqueIncoming[j]);
          processedIncoming.add(uniqueIncoming[j].localId);
        }
      }

      incomingGroups.push(group);
    }

    // For each incoming group, find matching existing contacts
    for (const incomingContacts of incomingGroups) {
      const matchingExisting: Contact[] = [];
      for (const existingContact of existing) {
        for (const incomingContact of incomingContacts) {
          if (
            ContactLogic.matchByCommonNameParts(
              incomingContact,
              existingContact
            )
          ) {
            matchingExisting.push(existingContact);
            break; // Only add each existing contact once
          }
        }
      }

      // Create a group if:
      // - 2+ incoming contacts (incoming-only duplicates), OR
      // - 1+ incoming AND matching existing (incoming matches existing)
      if (incomingContacts.length >= 2 || matchingExisting.length > 0) {
        const group: DuplicateContactGroup = [
          // Add incoming contacts first
          ...incomingContacts.map((contact: Contact) => ({
            contact,
            source: 'incoming' as const,
          })),
          // Then add existing contacts (if any)
          ...matchingExisting.map((contact: Contact) => ({
            contact,
            source: 'existing' as const,
          })),
        ];
        groups.push(group);
      }
    }

    return groups;
  },

  /**
   * Merge multiple contacts following these rules:
   * - existing contacts are merged first and take priority
   * - incoming contacts are merged last
   * Takes a DuplicateContactGroup and extracts all contacts from it, merging them in source order (existing, incoming)
   */
  mergeContacts(group: DuplicateContactGroup): Contact {
    // Extract all contacts from the group, ordering by source: existing, then incoming
    const sourceOrder: Record<DuplicateContact['source'], number> = {
      existing: 0,
      incoming: 1,
    };
    const contacts = [...group]
      .sort((a, b) => sourceOrder[a.source] - sourceOrder[b.source])
      .map(item => item.contact);

    if (contacts.length === 0) {
      throw new Error('Cannot merge empty contact group');
    }
    if (contacts.length === 1) {
      return contacts[0];
    }

    // Start with first contact
    const first = contacts[0];
    let mergedPrefix = first.prefix.trim();
    let mergedFirstName = first.firstName.trim();
    let mergedMiddleName = first.middleName.trim();
    let mergedLastName = first.lastName.trim();
    let mergedSuffix = first.suffix.trim();
    let mergedNickname = first.nickname.trim();

    // Merge name fields: prefer non-empty values from earlier contacts
    for (let i = 1; i < contacts.length; i++) {
      const contact = contacts[i];
      if (!mergedPrefix && contact.prefix.trim()) {
        mergedPrefix = contact.prefix.trim();
      }
      if (!mergedFirstName && contact.firstName.trim()) {
        mergedFirstName = contact.firstName.trim();
      }
      if (!mergedMiddleName && contact.middleName.trim()) {
        mergedMiddleName = contact.middleName.trim();
      }
      if (!mergedLastName && contact.lastName.trim()) {
        mergedLastName = contact.lastName.trim();
      }
      if (!mergedSuffix && contact.suffix.trim()) {
        mergedSuffix = contact.suffix.trim();
      }
      if (!mergedNickname && contact.nickname.trim()) {
        mergedNickname = contact.nickname.trim();
      }
    }

    // Merge phone numbers
    const allTels = contacts.map(c => [...c.tels]);
    const mergedTels = PhoneLogic.merge(allTels);

    // Merge listing categories: combine arrays, deduplicate
    const listingCategorySet = new Set<string>();
    for (const contact of contacts) {
      for (const category of contact.listingCategories) {
        listingCategorySet.add(category);
      }
    }
    const mergedListingCategories = Array.from(listingCategorySet);

    // Preserve fnPassthru from the first contact that has one
    const mergedFnPassthru = contacts.find(c => c.fnPassthru)?.fnPassthru;

    // Preserve the organization (company + unit + sub-unit) as a unit from the
    // first contact that has a company name, so the components stay consistent.
    const mergedOrg = contacts.find(c => c.companyName?.trim());

    return {
      localId: first.localId,
      uid: first.uid,
      prefix: mergedPrefix,
      firstName: mergedFirstName,
      middleName: mergedMiddleName,
      lastName: mergedLastName,
      suffix: mergedSuffix,
      nickname: mergedNickname,
      ...(mergedOrg?.companyName && { companyName: mergedOrg.companyName }),
      ...(mergedOrg?.department && { department: mergedOrg.department }),
      ...(mergedOrg?.subDepartment && {
        subDepartment: mergedOrg.subDepartment,
      }),
      tels: mergedTels,
      listingCategories: mergedListingCategories,
      ...(mergedFnPassthru && { fnPassthru: mergedFnPassthru }),
    };
  },

  /**
   * ContactToResolveLogic - Service functions for ContactToResolve operations
   */

  /**
   * Sort contacts by source order: incoming first, then existing
   */
  sortBySource(contacts: DuplicateContactGroup): DuplicateContactGroup {
    const sourceOrder: Record<DuplicateContact['source'], number> = {
      incoming: 0,
      existing: 1,
    };

    return [...contacts].sort((a, b) => {
      return sourceOrder[a.source] - sourceOrder[b.source];
    });
  },

  /**
   * Extract and return only the Contact objects for a given source
   */
  getBySource(
    contacts: DuplicateContactGroup,
    source: DuplicateContact['source']
  ): Contact[] {
    return contacts.filter(c => c.source === source).map(c => c.contact);
  },

  /**
   * Convenience wrapper to get incoming contacts
   */
  getIncoming(contacts: DuplicateContactGroup): Contact[] {
    return ContactLogic.getBySource(contacts, 'incoming');
  },

  /**
   * Convenience wrapper to get existing contacts
   */
  getExisting(contacts: DuplicateContactGroup): Contact[] {
    return ContactLogic.getBySource(contacts, 'existing');
  },

  /**
   * True when the contact has at least one US phone usable for SMS sharing.
   * Mirrors the rule used by getPrimaryPhone / toContactLike, so an "unreachable"
   * contact is one those would reject.
   */
  isReachable(contact: Contact): boolean {
    return ContactLogic.getPrimaryPhone(contact) !== null;
  },

  /**
   * Parse the raw result of a device Contact Picker selection
   * (navigator.contacts.select(['name', 'tel'], { multiple: true })) into
   * reachable Contacts, deduplicating within the selection. Returns counts of
   * skipped entries so callers can surface a warning toast:
   *   - noSmsableCount: entries dropped because they had no SMS-able phone
   *   - droppedPhoneCount: duplicate phone numbers removed from kept contacts
   *     (a common quirk of PWA contact pickers)
   */
  parseDeviceContactSelection(
    results: Array<{ name?: string | string[]; tel?: string | string[] }>
  ): {
    contacts: Contact[];
    noSmsableCount: number;
    droppedPhoneCount: number;
  } {
    let noSmsableCount = 0;
    let droppedPhoneCount = 0;

    const parsedContacts: Contact[] = [];
    for (const r of results || []) {
      const contactName = Array.isArray(r.name) ? r.name[0] : r.name || '';
      const tels: string[] = Array.isArray(r.tel)
        ? r.tel
        : r.tel
          ? [r.tel]
          : [];

      const result = ContactLogic.createReachableFromFormattedName(
        contactName,
        tels
      );

      if (!result) {
        noSmsableCount++;
        continue;
      }

      const { contact: newContact, duplicatePhones } = result;
      droppedPhoneCount += duplicatePhones.length;
      parsedContacts.push(newContact);
    }

    const contacts = parsedContacts.filter((contact, index) => {
      const name = ContactLogic.fullName(contact);
      return !parsedContacts.some((other, otherIndex) => {
        if (index === otherIndex) return false;
        if (ContactLogic.fullName(other) !== name) return false;
        if (!ContactLogic.hasPhoneSubsetOf(contact, other)) return false;
        if (ContactLogic.hasPhoneSubsetOf(other, contact)) {
          return otherIndex < index;
        }
        return true;
      });
    });

    return { contacts, noSmsableCount, droppedPhoneCount };
  },

  toContactLike(contact: Contact): ContactLike {
    const primaryPhone = ContactLogic.getPrimaryPhone(contact);
    if (!primaryPhone) {
      throw new Error('Contact must have at least one phone number');
    }
    const digits = primaryPhone.replace(/\D/g, '');
    const phone =
      digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
    return {
      name: ContactLogic.displayName(contact),
      phone,
      ...(contact.snowballId && { userId: contact.snowballId }),
    };
  },
};
